import type { TextEngineService } from '../../text-engine';
import type { FontLibrary } from '../library';
import { FontSubsetCache, type FontSubsetCacheEntry } from './cache';
import { FontSubsetStatus, type FontSubset, type RegisterTarget } from './types';
import { createOwnerRequestKey, createSubsetName, fetchSubsetBytes, isCharSetCovered, normalizeChars } from './utils';

/**
 * 为子集记录和字节缓存生成按注册目标隔离的键。
 *
 * 同一 subsetName 可能同时用于 engine 和 document，但两者使用的字体格式和注册生命周期彼此独立，
 * 不能共享 subsets 或 cache 记录。使用 NUL 分隔可避免普通字体名中的可见分隔符造成边界歧义。
 *
 * @param target - 注册目标
 * @param subsetName - 子集名称
 * @returns 按目标隔离的键
 */
const createSubsetKey = (target: RegisterTarget, subsetName: string) => `${target}\u0000${subsetName}`;

export class FontSubsetManager {
    /**
     * 注册维度：按 registerTarget + subsetName 多份并存的子集记录
     *
     * key: targetKey
     * value: 子集记录
     */
    private subsets = new Map<string, FontSubset>();

    /**
     * 子集字节缓存（LRU，见 {@link FontSubsetCache}）：以 subsetName 为键缓存已拉取的字节，
     * 用于同一 subsetName 再次出现时免重新拉取；容量受限，与引擎注册生命周期解耦
     */
    private cache = new FontSubsetCache();

    /**
     * 同一请求单元在请求未完成前再次请求新字符集时，用它取消旧请求并复用相同请求，
     * 避免晚返回的旧子集覆盖更新后的子集状态
     *
     * 按注册目标分桶，每个 Map 的 key 是调用方提供的不透明 requestKey。
     */
    private inFlight: Record<
        RegisterTarget,
        Map<string, { subsetName: string; controller: AbortController; promise: Promise<FontSubset | null> }>
    > = {
        engine: new Map(),
        document: new Map(),
    };

    constructor(
        private readonly textEngine: TextEngineService,
        private readonly library: FontLibrary,
    ) {}

    /**
     * 按子集名称获取已有子集记录。
     *
     * @param subsetName - 子集名称
     * @param target - [可选] 注册目标，默认为 engine
     * @returns 已有子集记录；不存在时返回 null
     */
    public getByName(subsetName: string | undefined, target: RegisterTarget = 'engine'): FontSubset | null {
        if (!subsetName) return null;

        const key = createSubsetKey(target, subsetName);
        return this.subsets.get(key) ?? null;
    }

    /**
     * 判断指定子集是否已注册且覆盖指定字符。
     *
     * @param subsetName - 子集名称
     * @param chars - 指定字符
     * @param target - [可选] 注册目标，默认为 engine
     * @returns 是否已注册且覆盖指定字符
     */
    public hasAvailable(subsetName: string | undefined, chars: string, target: RegisterTarget = 'engine'): boolean {
        const subset = this.getByName(subsetName, target);

        if (!subset) return false;
        if (subset.status !== FontSubsetStatus.Registered) return false;

        return isCharSetCovered(subset.chars, chars);
    }

    /**
     * 查找已注册且覆盖指定字符的字体子集。
     *
     * @param fontFamily - 字体族名称
     * @param chars - 指定字符
     * @param target - [可选] 注册目标，默认为 engine
     * @returns 字符最少的可用子集；不存在时返回 null
     */
    public findAvailable(fontFamily: string, chars: string, target: RegisterTarget = 'engine'): FontSubset | null {
        const normalizedChars = normalizeChars(chars);
        if (!normalizedChars) return null;

        const subsetKey = createSubsetKey(target, '');
        let available: FontSubset | null = null;

        for (const [key, subset] of this.subsets) {
            // 不同注册目标的字体资源不可混用。
            if (!key.startsWith(subsetKey)) continue;

            // 只复用同字体且已完成注册的子集。
            if (subset.fontFamily !== fontFamily || subset.status !== FontSubsetStatus.Registered) continue;

            // 候选子集必须完整覆盖本次请求字符。
            if (!isCharSetCovered(subset.chars, normalizedChars)) continue;

            // 首个可用子集作为初始候选；后续优先保留字符数更少的子集。
            if (!available || [...subset.chars].length < [...available.chars].length) available = subset;
        }

        return available;
    }

    /**
     * 判断指定字体与字符集是否已有加载终态。
     * @param fontFamily - 字体族名称
     * @param chars - 指定字符
     * @param target - [可选] 注册目标
     * @returns 是否已有注册成功或失败记录
     */
    public hasSettled(fontFamily: string, chars: string, target: RegisterTarget = 'engine'): boolean {
        const normalizedChars = normalizeChars(chars);
        if (!normalizedChars) return false;

        const subsetName = createSubsetName(fontFamily, normalizedChars);
        const key = createSubsetKey(target, subsetName);
        return this.subsets.has(key);
    }

    /**
     * 为某字体拉取并注册，返回子集记录（不绑定任何图层映射）。
     *
     * @param fontFamily - 字体族名称
     * @param chars - 需要覆盖的字符
     * @param registerTo - 注册目标
     * @param ownerId - [可选] 请求拥有者；同一拥有者同一字体的新请求会取代旧请求
     * @returns 子集记录；失败时返回 null
     */
    public async load(
        fontFamily: string,
        chars: string,
        registerTo: RegisterTarget = 'engine',
        ownerId?: string,
    ): Promise<FontSubset | null> {
        const normalizedChars = normalizeChars(chars);
        if (!normalizedChars) return null;

        if (registerTo === 'engine') {
            const engine = this.textEngine.getEngine();
            if (!engine || engine.isDisposed) return this.recordFailure(fontFamily, normalizedChars, registerTo);
        }

        const subsetName = createSubsetName(fontFamily, normalizedChars);
        const subsetKey = createSubsetKey(registerTo, subsetName);

        // 若同一 subsetName 已有终态，则幂等复用，不重复请求/register。
        const existing = this.subsets.get(subsetKey);
        if (existing) return existing;

        const requests = this.inFlight[registerTo];
        const requestKey = ownerId === undefined ? subsetName : createOwnerRequestKey(ownerId, fontFamily);

        // 若同一请求单元已存在 in-flight 请求，则复用，否则取消旧请求并创建新请求
        const inflight = requests.get(requestKey);
        if (inflight) {
            if (inflight.subsetName === subsetName) return inflight.promise;
            inflight.controller.abort();
        }

        // 创建新的请求单元
        const controller = new AbortController();
        const promise = (async () => {
            try {
                const entry = await this.resolveEntry(fontFamily, normalizedChars, subsetName, registerTo, controller);

                if (controller.signal.aborted) return this.subsets.get(subsetKey) ?? null;

                const settled = this.subsets.get(subsetKey);
                if (settled) return settled;

                await this.register(entry, registerTo);

                return this.upsertRecord(subsetKey, {
                    fontFamily,
                    chars: normalizedChars,
                    name: subsetName,
                    status: FontSubsetStatus.Registered,
                });
            } catch {
                if (controller.signal.aborted) return this.subsets.get(subsetKey) ?? null;
                return this.recordFailure(fontFamily, normalizedChars, registerTo);
            }
        })().finally(() => {
            // 当本次请求结束时（无论成功还是失败），把它从 inFlight 中移除
            const inflight = requests.get(requestKey);
            if (inflight && inflight.controller === controller) requests.delete(requestKey);
        });

        requests.set(requestKey, { subsetName, controller, promise });
        return promise;
    }

    /**
     * 从缓存解析出可注册条目，未命中时下载并缓存。
     * @param fontFamily - 字体族名称
     * @param chars - 字符集
     * @param subsetName - 子集名称
     * @param registerTo - 注册目标
     * @param controller - 请求控制器
     * @returns 子集字节缓存条目
     */
    private async resolveEntry(
        fontFamily: string,
        chars: string,
        subsetName: string,
        registerTo: RegisterTarget,
        controller: AbortController,
    ): Promise<FontSubsetCacheEntry> {
        const preferredFormats = registerTo === 'document' ? (['woff2', 'ttf'] as const) : (['ttf'] as const);
        const source = this.library.resolveSource(fontFamily, preferredFormats);
        if (!source) {
            throw new Error(`Font ${fontFamily} ${preferredFormats.join(' or ')} source not found.`);
        }

        const key = createSubsetKey(registerTo, subsetName);
        const cached = this.cache.get(key);
        if (cached) return cached;

        const bytes = await fetchSubsetBytes(source.url, chars, source.format, controller);
        const entry = { fontFamily, chars, name: subsetName, bytes };

        this.cache.set(key, entry);

        return entry;
    }

    /**
     * 将字体子集字节注册到指定目标。
     * @param entry - 子集字节缓存条目
     * @param registerTo - 注册目标
     */
    private async register(entry: FontSubsetCacheEntry, registerTo: RegisterTarget): Promise<void> {
        if (registerTo === 'document') {
            if (typeof FontFace === 'undefined' || typeof document === 'undefined' || !document.fonts) {
                throw new Error('Document font loading is not supported.');
            }

            const fontFace = new FontFace(entry.name, new Uint8Array(entry.bytes).buffer, { display: 'swap' });
            document.fonts.add(fontFace);

            await fontFace.load();
            return;
        }

        const engine = this.textEngine.getEngine();
        if (!engine || engine.isDisposed) throw new Error('Text engine is not available.');

        const result = engine.registerFont(entry.name, entry.bytes);
        if (!result.ok) throw result.error;
        if (!engine.getFontFamilyName(entry.name)) throw new Error('Text engine did not return a subset family name.');

        this.textEngine.markFontStateChanged();
    }

    /**
     * 取消 in-flight 请求并清空运行时状态，避免卸载后继续注册字体。
     */
    public dispose() {
        for (const requests of Object.values(this.inFlight)) {
            for (const inflight of requests.values()) inflight.controller.abort();
            requests.clear();
        }
        this.cache.clear();
        this.subsets.clear();
    }

    /**
     * update/insert 按目标子集 key 写入记录
     *
     * @param subsetKey - 目标子集 key
     * @param partial - 子集记录的部分属性
     * @returns 已写入的子集记录
     */
    private upsertRecord(subsetKey: string, record: FontSubset): FontSubset {
        this.subsets.set(subsetKey, record);
        return record;
    }

    /**
     * 记录子集加载或注册失败：保留按注册目标 + subsetName 的失败信息。
     *
     * @param fontFamily - 字体族名称
     * @param chars - 字符集
     * @param registerTo - 注册目标
     * @returns 已写入的子集记录
     */
    private recordFailure(fontFamily: string, chars: string, registerTo: RegisterTarget): FontSubset {
        const subsetName = createSubsetName(fontFamily, chars);
        const subsetKey = createSubsetKey(registerTo, subsetName);
        const existing = this.subsets.get(subsetKey);

        // 已注册的同名子集失败（如刷新过期）时不降级状态。
        if (existing?.status === FontSubsetStatus.Registered) return existing;

        return this.upsertRecord(subsetKey, {
            fontFamily,
            chars,
            name: subsetName,
            status: FontSubsetStatus.Error,
        });
    }
}
